import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, XMarkIcon, EyeIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ds * { box-sizing:border-box; }
  .ds { font-family:'Sora',sans-serif; color:#1a1d2e; animation:dsFade .4s ease both; }
  @keyframes dsFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .ds-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .ds-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .ds-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }

  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); transform:translateY(-1px); }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover { background:#ef4444; color:white; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }

  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }

  .ds-card { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f5f6fb; gap:10px; flex-wrap:wrap; }
  .ds-card:last-child { border-bottom:none; }
  .ds-card:hover { background:#fafbff; }
  .ds-card-left { display:flex; align-items:center; gap:12px; flex:1; min-width:0; }
  .ds-icon { width:36px; height:36px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:1.1rem; flex-shrink:0; }
  .ds-icon.file   { background:#fee2e2; }
  .ds-icon.manual { background:#dbeafe; }
  .ds-card-title { font-size:.84rem; font-weight:600; color:#0f1123; }
  .ds-card-meta  { font-size:.72rem; color:#9094a8; margin-top:3px; }
  .ds-card-actions { display:flex; gap:6px; align-items:center; flex-shrink:0; flex-wrap:wrap; }
  .visible-badge { display:inline-flex; font-size:.65rem; font-weight:600; padding:2px 8px; border-radius:99px; }
  .visible-badge.yes { background:#ecfdf5; color:#10c98e; }
  .visible-badge.no  { background:#f3f4f6; color:#6b7280; }

  .ds-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .ds-loading { display:flex; align-items:center; justify-content:center; height:150px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; max-width:780px; max-height:90vh; overflow-y:auto; overflow-x:hidden; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:18px; flex-wrap:wrap; }

  .mode-toggle { display:flex; gap:6px; background:#f0f2f7; border-radius:10px; padding:4px; margin-bottom:16px; }
  .mode-btn { flex:1; padding:7px 0; border-radius:7px; border:none; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .15s; background:transparent; color:#9094a8; }
  .mode-btn.active { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }

  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:14px; }
  .form-grid > *, .fg > * { min-width:0; }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-field.span2 { grid-column:1/-1; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-select:focus { border-color:#3b6ef8; background:white; }

  .entry-table { width:100%; border-collapse:collapse; margin-bottom:8px; }
  .entry-table th { font-size:.64rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; padding:0 4px 6px; text-align:center !important; white-space:nowrap; }
  .entry-table td { padding:3px 4px; text-align:center !important; }
  .entry-input { font-family:'Sora',sans-serif; font-size:.78rem; padding:7px 9px; border:1.5px solid #e0e4f0; border-radius:8px; background:#f8f9fc; outline:none; width:100%; }
  .entry-input:focus { border-color:#3b6ef8; background:white; }

  .entry-cards { display:none; }
  .entry-card { background:#f8f9fc; border-radius:10px; border:1px solid #e0e4f0; padding:10px 12px; margin-bottom:8px; }
  .entry-card-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-bottom:8px; }
  .entry-card-field { display:flex; flex-direction:column; gap:3px; }
  .entry-card-label { font-size:.62rem; font-weight:700; color:#9094a8; text-transform:uppercase; }

  .drop-zone { border:2px dashed #d0d5e8; border-radius:10px; padding:20px; text-align:center; cursor:pointer; transition:all .15s; background:#fafbfc; }
  .drop-zone:hover { border-color:#3b6ef8; background:#f0f4ff; }
  .drop-zone.has-file { border-color:#10c98e; background:#ecfdf5; }

  @media (max-width:768px) {
    .ds-title { font-size:1.1rem; }
    .ds-card { padding:12px 14px; }
    .ds-card-actions { width:100%; justify-content:flex-end; }
    .modal { padding:16px; }
    .form-grid { grid-template-columns:1fr; }
    .form-field.span2 { grid-column:1; }
    .modal-btns { justify-content:stretch; }
    .modal-btns .btn { flex:1; justify-content:center; }
    .entry-table-wrap { display:none; }
    .entry-cards { display:block; }
  }
`;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'}) : '';
const curYear = () => { const y = new Date().getFullYear(); return `${y}-${y+1}`; };

// "14:30" -> "2:30 PM"
const formatTime12h = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2,'0')} ${period}`;
};

// "09:00" + "11:30" -> "2h 30m"
const calcDuration = (start, end) => {
  if (!start || !end) return '';
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) mins += 24 * 60;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

// "2:30 PM" -> "14:30"
const parseTime12hTo24h = (t) => {
  const m = (t || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return '';
  let h = parseInt(m[1], 10);
  const period = m[3].toUpperCase();
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2,'0')}:${m[2]}`;
};

// "2:30 PM - 4:00 PM" -> { startTime:'14:30', endTime:'16:00' }
const parseTimeRange = (range) => {
  if (!range) return { startTime:'', endTime:'' };
  const [start, end] = range.split(' - ').map(s => s.trim());
  return { startTime: parseTime12hTo24h(start), endTime: parseTime12hTo24h(end) };
};

const DatesheetPage = ({ isAdmin = false }) => {
  const [datesheets,   setDatesheets]   = useState([]);
  const [classes,      setClasses]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [open,         setOpen]         = useState(false);
  const [editingId,    setEditingId]    = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [uploadMode,   setUploadMode]   = useState('file');
  const [selectedFile, setSelectedFile] = useState(null);
  const [entries,      setEntries]      = useState([{ subject:'', date:'', startTime:'', endTime:'' }]);
  const [form, setForm] = useState({ title:'', academicYear: curYear(), classId:'', section:'', visibleToStudents: false });
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    fetchDatesheets();
    if (isAdmin) {
      api.get('/admin/classes')
        .then(r => setClasses(Array.isArray(r.data) ? r.data : (r.data?.classes || [])))
        .catch(() => toast.error('Failed to load classes'));
    }
  }, [showArchived]);

  const fetchDatesheets = async () => {
    try {
      setLoading(true);
      const ep = isAdmin ? '/admin/datesheets' : '/teacher/datesheets';
      const params = isAdmin && showArchived ? '?includeArchived=true' : '';
      const { data } = await api.get(`${ep}${params}`);
      setDatesheets(data);
    } catch { toast.error('Failed to load'); }
    finally  { setLoading(false); }
  };

  const handleToggleArchive = async (d) => {
    try {
      await api.put(`/admin/datesheets/${d._id}`, { archived: !d.archived });
      toast.success(!d.archived ? 'Datesheet archived' : 'Datesheet restored');
      fetchDatesheets();
    } catch { toast.error('Failed'); }
  };

  const selectedClass = classes.find(c => String(c._id) === String(form.classId));

  // Subjects available for the entry dropdown: the selected class's subjects,
  // or every subject across all classes (deduped) when no class is chosen.
  // Some class records store subjects as one comma-separated string per entry
  // rather than one entry per subject, so split+trim defensively either way.
  const splitSubjects = (arr) => (arr || []).flatMap(s => String(s).split(',').map(x => x.trim())).filter(Boolean);
  const availableSubjects = form.classId
    ? [...new Set(splitSubjects(selectedClass?.subjects))]
    : [...new Set(classes.flatMap(c => splitSubjects(c.subjects)))].sort();

  // Sections available once a class is chosen — "All Sections" is always the default option.
  const availableSections = selectedClass?.sections || [];

  const addEntry    = () => setEntries([...entries, { subject:'', date:'', startTime:'', endTime:'' }]);
  const removeEntry = (i) => setEntries(entries.filter((_,idx) => idx !== i));
  const updateEntry = (i, field, val) => setEntries(prev => { const n=[...prev]; n[i]={...n[i],[field]:val}; return n; });

  const resetForm = () => {
    setEditingId(null); setSelectedFile(null);
    setUploadMode('file');
    setEntries([{ subject:'', date:'', startTime:'', endTime:'' }]);
    setForm({ title:'', academicYear: curYear(), classId:'', section:'', visibleToStudents: false });
  };

  const openCreate = () => { resetForm(); setOpen(true); };

  const openEdit = (d) => {
    setEditingId(d._id);
    setUploadMode(d.type);
    setSelectedFile(null);
    setForm({
      title: d.title || '', academicYear: d.academicYear || curYear(),
      classId: d.class?._id ? String(d.class._id) : '', section: d.section || '',
      visibleToStudents: !!d.visibleToStudents,
    });
    setEntries(
      d.type === 'manual' && d.entries?.length
        ? d.entries.map(e => ({ subject: e.subject || '', date: e.date || '', ...parseTimeRange(e.time) }))
        : [{ subject:'', date:'', startTime:'', endTime:'' }]
    );
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.title) { toast.error('Title is required'); return; }
    if (uploadMode === 'file' && !editingId && !selectedFile) { toast.error('Please select a file'); return; }
    try {
      setSaving(true);
      const fd = new FormData();
      fd.append('title',             form.title);
      fd.append('academicYear',      form.academicYear);
      fd.append('classId',           form.classId);
      fd.append('section',          form.classId ? form.section : '');
      fd.append('type',              uploadMode);
      fd.append('visibleToStudents', String(form.visibleToStudents));
      if (uploadMode === 'file' && selectedFile) fd.append('file', selectedFile);
      if (uploadMode === 'manual') {
        const preparedEntries = entries.map(e => ({
          subject:  e.subject,
          date:     e.date,
          time:     e.startTime && e.endTime ? `${formatTime12h(e.startTime)} - ${formatTime12h(e.endTime)}` : '',
          duration: calcDuration(e.startTime, e.endTime),
        }));
        fd.append('entries', JSON.stringify(preparedEntries));
      }
      if (editingId) await api.put(`/admin/datesheets/${editingId}`, fd, { headers:{'Content-Type':'multipart/form-data'} });
      else           await api.post('/admin/datesheets', fd, { headers:{'Content-Type':'multipart/form-data'} });
      toast.success(editingId ? 'Datesheet updated' : 'Datesheet created');
      setOpen(false); resetForm();
      fetchDatesheets();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleToggleVisible = async (d) => {
    try {
      await api.put(`/admin/datesheets/${d._id}`, { visibleToStudents: !d.visibleToStudents });
      toast.success(!d.visibleToStudents ? 'Now visible to students' : 'Hidden from students');
      fetchDatesheets();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="ds">
      <style>{style}</style>

      <div className="ds-header">
        <div>
          <div className="ds-title">Datesheets</div>
          <div className="ds-sub">
            {isAdmin
              ? 'Upload and manage exam schedules for students'
              : 'Exam schedules published by admin'}
          </div>
        </div>
        {/* Only admin sees the Add button + archive filter */}
        {isAdmin && (
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <label style={{display:'flex',alignItems:'center',gap:6,fontSize:'.78rem',color:'#6b7194',cursor:'pointer'}}>
              <input type="checkbox" checked={showArchived} onChange={e=>setShowArchived(e.target.checked)} />
              Show Archived
            </label>
            {!showArchived && (
              <button className="btn btn-blue" onClick={openCreate}>
                <PlusIcon />Add Datesheet
              </button>
            )}
          </div>
        )}
      </div>

      <div className="panel">
        <div className="panel-head">
          <div className="panel-title">{showArchived ? 'Archived ' : ''}Datesheets ({datesheets.length})</div>
        </div>
        {loading ? (
          <div className="ds-loading"><div className="spinner"/>Loading…</div>
        ) : datesheets.length === 0 ? (
          <div className="ds-empty">
            {isAdmin
              ? (showArchived ? 'No archived datesheets.' : 'No datesheets yet. Click "Add Datesheet" to create one.')
              : 'No datesheets published yet.'}
          </div>
        ) : datesheets.map(d => (
          <div key={d._id} className="ds-card">
            <div className="ds-card-left">
              <div className={`ds-icon ${d.type}`}>{d.type==='file' ? '📄' : '📅'}</div>
              <div style={{minWidth:0}}>
                <div className="ds-card-title">{d.title}</div>
                <div className="ds-card-meta">
                  {d.class?.name || 'All classes'}{d.section ? ` — Sec ${d.section}` : ''} · {d.academicYear}
                  {isAdmin && ` · by ${d.createdBy?.name}`}
                </div>
                <div style={{marginTop:4,display:'flex',gap:6,flexWrap:'wrap'}}>
                  <span className={`visible-badge ${d.visibleToStudents?'yes':'no'}`}>
                    {d.visibleToStudents ? '👁 Visible' : '🚫 Hidden'}
                  </span>
                  {d.archived && <span className="visible-badge no">🗄 Archived</span>}
                </div>
              </div>
            </div>
            <div className="ds-card-actions">
              {d.fileUrl && (
                <a href={d.fileUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                  <EyeIcon />View
                </a>
              )}
              {/* Edit / Publish / Hide — admin only */}
              {isAdmin && (
                <>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(d)}>
                    <PencilSquareIcon />Edit
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => handleToggleVisible(d)}>
                    {d.visibleToStudents ? 'Hide' : 'Publish'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => handleToggleArchive(d)}>
                    {d.archived ? 'Restore' : 'Archive'}
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create Modal — admin only */}
      {isAdmin && open && createPortal(
        <div className="modal-backdrop" onClick={() => setOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">{editingId ? 'Edit Datesheet' : 'Create Datesheet'}</div>
              <button className="modal-close" onClick={() => setOpen(false)}><XMarkIcon /></button>
            </div>

            <div className="mode-toggle">
              <button className={`mode-btn ${uploadMode==='file'?'active':''}`} disabled={!!editingId} onClick={() => setUploadMode('file')}>📄 Upload File</button>
              <button className={`mode-btn ${uploadMode==='manual'?'active':''}`} disabled={!!editingId} onClick={() => setUploadMode('manual')}>📅 Manual Entry</button>
            </div>

            <div className="form-grid">
              <div className="form-field span2">
                <label className="form-label">Title *</label>
                <input type="text" className="form-input" value={form.title}
                  onChange={e=>setForm({...form,title:e.target.value})} placeholder="e.g. Final Exams 2025-2026" />
              </div>
              <div className="form-field">
                <label className="form-label">Academic Year</label>
                <input type="text" className="form-input" value={form.academicYear}
                  onChange={e=>setForm({...form,academicYear:e.target.value})} />
              </div>
              <div className="form-field">
                <label className="form-label">Class (optional)</label>
                <select className="form-select" value={form.classId} onChange={e=>setForm({...form,classId:e.target.value,section:''})}>
                  <option value="">All classes</option>
                  {classes.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label className="form-label">Section</label>
                <select className="form-select" value={form.section} onChange={e=>setForm({...form,section:e.target.value})} disabled={!form.classId}>
                  <option value="">All Sections</option>
                  {availableSections.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-field span2" style={{flexDirection:'row',alignItems:'center',gap:10}}>
                <input type="checkbox" id="vis" checked={form.visibleToStudents}
                  onChange={e=>setForm({...form,visibleToStudents:e.target.checked})} />
                <label htmlFor="vis" style={{fontSize:'.82rem',fontWeight:500,cursor:'pointer',color:'#1a1d2e'}}>
                  Visible to students immediately
                </label>
              </div>
            </div>

            {uploadMode === 'file' ? (
              <div className={`drop-zone ${selectedFile?'has-file':''}`}
                onClick={() => document.getElementById('ds-file').click()}>
                <input id="ds-file" type="file" style={{display:'none'}}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={e => setSelectedFile(e.target.files[0])} />
                {selectedFile ? (
                  <div>
                    <div style={{fontSize:'1.4rem',marginBottom:6}}>📄</div>
                    <div style={{fontWeight:600,fontSize:'.82rem'}}>{selectedFile.name}</div>
                    <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:3}}>Click to change file</div>
                  </div>
                ) : editingId ? (
                  <div>
                    <div style={{fontSize:'1.4rem',marginBottom:6}}>📄</div>
                    <div style={{fontWeight:600,fontSize:'.82rem'}}>Current file kept</div>
                    <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:3}}>Click to replace it</div>
                  </div>
                ) : (
                  <div>
                    <div style={{fontSize:'1.6rem',marginBottom:8}}>☁️</div>
                    <div style={{fontWeight:600,fontSize:'.82rem',color:'#1a1d2e'}}>Click to upload</div>
                    <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:4}}>PDF, Image, Word document</div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="entry-table-wrap">
                  <table className="entry-table">
                    <thead>
                      <tr><th>Subject</th><th>Date</th><th>From</th><th>To</th><th>Duration</th><th></th></tr>
                    </thead>
                    <tbody>
                      {entries.map((e,i) => (
                        <tr key={i}>
                          <td>
                            <select className="entry-input" value={e.subject} onChange={v=>updateEntry(i,'subject',v.target.value)}>
                              <option value="">Select…</option>
                              {availableSubjects.map(s=><option key={s} value={s}>{s}</option>)}
                            </select>
                          </td>
                          <td><input type="date" className="entry-input" value={e.date} onChange={v=>updateEntry(i,'date',v.target.value)} /></td>
                          <td><input type="time" className="entry-input" value={e.startTime} onChange={v=>updateEntry(i,'startTime',v.target.value)} /></td>
                          <td><input type="time" className="entry-input" value={e.endTime} onChange={v=>updateEntry(i,'endTime',v.target.value)} /></td>
                          <td style={{fontSize:'.76rem',color:'#6b7194',whiteSpace:'nowrap'}}>{calcDuration(e.startTime,e.endTime) || '—'}</td>
                          <td>
                            <button style={{width:28,height:28,border:'1px solid #fecaca',borderRadius:7,background:'#fef2f2',color:'#ef4444',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}
                              onClick={() => removeEntry(i)}><XMarkIcon style={{width:12,height:12}} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="entry-cards">
                  {entries.map((e,i) => (
                    <div key={i} className="entry-card">
                      <div className="entry-card-grid">
                        <div className="entry-card-field" style={{gridColumn:'1/-1'}}>
                          <span className="entry-card-label">Subject</span>
                          <select className="entry-input" value={e.subject} onChange={v=>updateEntry(i,'subject',v.target.value)}>
                            <option value="">Select…</option>
                            {availableSubjects.map(s=><option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div className="entry-card-field">
                          <span className="entry-card-label">Date</span>
                          <input type="date" className="entry-input" value={e.date} onChange={v=>updateEntry(i,'date',v.target.value)} />
                        </div>
                        <div className="entry-card-field">
                          <span className="entry-card-label">From</span>
                          <input type="time" className="entry-input" value={e.startTime} onChange={v=>updateEntry(i,'startTime',v.target.value)} />
                        </div>
                        <div className="entry-card-field">
                          <span className="entry-card-label">To</span>
                          <input type="time" className="entry-input" value={e.endTime} onChange={v=>updateEntry(i,'endTime',v.target.value)} />
                        </div>
                        <div className="entry-card-field" style={{gridColumn:'1/-1'}}>
                          <span className="entry-card-label">Duration</span>
                          <span style={{fontSize:'.78rem',color:'#6b7194',fontWeight:600}}>{calcDuration(e.startTime,e.endTime) || '—'}</span>
                        </div>
                      </div>
                      <button className="btn btn-red btn-sm" style={{width:'100%',justifyContent:'center'}} onClick={() => removeEntry(i)}>
                        <XMarkIcon />Remove
                      </button>
                    </div>
                  ))}
                </div>

                <button className="btn btn-ghost btn-sm" onClick={addEntry} style={{marginTop:4}}><PlusIcon />Add Row</button>
              </div>
            )}

            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleSubmit} disabled={saving}>
                {saving ? (editingId ? 'Saving…' : 'Creating…') : (editingId ? 'Save Changes' : 'Create Datesheet')}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};

export default DatesheetPage;